/*
  BJAF desktop menu (the DESKTOP MENU part of sections/bjaf-mobile-menu.liquid).

  The section already moved the buttons into the header (#bjaf-dm-nav into .bjaf-dm-slot). This script moves the
  panels (#bjaf-dm-layer) to the end of <body>, then opens one panel at a time under the header: one per category
  (Supplements, Grooming, Care), Shop by need, Help and the search panel (the header's search icon). Click a button
  to open or close; Escape, the dimmed page, scrolling away or going below 1280px closes. The search panel shows live
  results while typing (sections/bjaf-predictive-search). While open, the panel follows the header's bottom edge as the page
  scrolls. In a category panel, hovering or focusing a row in the side list ("All ..." or a sub-collection) shows
  its products.
*/
(() => {
  const layer = document.getElementById('bjaf-dm-layer');
  const header = document.querySelector('.shopify-section--header');
  if (!layer || !header) return;

  if (layer.parentElement !== document.body) document.body.appendChild(layer);

  const desktop = window.matchMedia('(min-width: 1280px)');
  const triggers = Array.from(document.querySelectorAll('[data-bjaf-dm-open]'));
  let current = null;
  let currentTrigger = null;
  let openedAtScroll = 0;

  const panelFor = (id) => document.getElementById(`bjaf-dm-${id}`);

  const place = (trigger) => {
    const top = Math.round(header.getBoundingClientRect().bottom);
    layer.style.setProperty('--bjaf-dm-top', `${Math.max(top, 0)}px`);
    if (trigger) layer.style.setProperty('--bjaf-dm-left', `${Math.round(trigger.getBoundingClientRect().left)}px`);
  };

  const close = (restoreFocus) => {
    if (!current) return;
    panelFor(current)?.setAttribute('hidden', '');
    layer.hidden = true;
    triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    if (restoreFocus && currentTrigger) currentTrigger.focus();
    current = null;
    currentTrigger = null;
  };

  const open = (id, trigger) => {
    const panel = panelFor(id);
    // The search panel also opens on phones and tablets (the header's left search icon); the rest is desktop only.
    if (!panel || (!desktop.matches && id !== 'search')) return false;
    if (current && current !== id) panelFor(current)?.setAttribute('hidden', '');
    place(trigger);
    layer.hidden = false;
    panel.removeAttribute('hidden');
    triggers.forEach((t) => t.setAttribute('aria-expanded', t.dataset.bjafDmOpen === id ? 'true' : 'false'));
    current = id;
    currentTrigger = trigger;
    openedAtScroll = window.scrollY;
    if (id === 'search') panel.querySelector('input[type="search"]')?.focus();
    return true;
  };

  triggers.forEach((trigger) => {
    trigger.addEventListener('click', (event) => {
      const id = trigger.dataset.bjafDmOpen;
      if (current === id) {
        event.preventDefault();
        close(false);
        return;
      }
      // The search icon is a link to the search page; it only opens the panel when the desktop menu can show it.
      if (open(id, trigger)) event.preventDefault();
    });
  });

  layer.addEventListener('click', (event) => {
    if (event.target.closest('[data-bjaf-dm-close]')) close(true);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && current) close(true);
  });

  window.addEventListener('scroll', () => {
    if (!current) return;
    if (Math.abs(window.scrollY - openedAtScroll) > 60) {
      close(false);
      return;
    }
    // Keep the panel against the header while the announcement bar scrolls away
    place();
  }, { passive: true });

  window.addEventListener('resize', () => {
    if (current) place(currentTrigger);
  });

  desktop.addEventListener('change', () => {
    if (!desktop.matches && current !== 'search') close(false);
  });

  // Search panel: live results while typing (Shopify predictive search, rendered by sections/bjaf-predictive-search).
  // Enter still opens the full search page. An empty field shows the panel's default groups again.
  const searchPanel = panelFor('search');
  const searchInput = searchPanel?.querySelector('input[type="search"]');
  const results = searchPanel?.querySelector('[data-bjaf-dm-results]');
  if (searchInput && results) {
    const defaults = Array.from(searchPanel.querySelectorAll('.bjaf-dm-search__group:not([data-bjaf-dm-results])'));
    const root = window.Shopify?.routes?.root || '/';
    let timer = null;
    let request = null;
    const showDefaults = (on) => {
      defaults.forEach((group) => { group.hidden = !on; });
      results.hidden = on;
    };
    const run = async () => {
      const q = searchInput.value.trim();
      request?.abort();
      if (!q) {
        results.replaceChildren();
        showDefaults(true);
        return;
      }
      request = new AbortController();
      const url = `${root}search/suggest?q=${encodeURIComponent(q)}&section_id=bjaf-predictive-search`
        + '&resources[type]=product,collection,page&resources[limit]=8&resources[options][unavailable_products]=last';
      try {
        const html = await fetch(url, { signal: request.signal }).then((r) => r.text());
        const content = new DOMParser().parseFromString(html, 'text/html').querySelector('.bjaf-dm-results');
        if (!content || searchInput.value.trim() !== q) return;
        results.replaceChildren(content);
        showDefaults(false);
      } catch (e) {
        // Aborted by newer typing, or offline: keep what is shown
      }
    };
    searchInput.addEventListener('input', () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(run, 180);
    });
  }

  // Category panels: the hovered or focused row in the side list shows its products (each panel on its own).
  layer.querySelectorAll('.bjaf-dm-panel--shop').forEach((panel) => {
    const rows = Array.from(panel.querySelectorAll('.bjaf-dm-shop__cat'));
    const views = Array.from(panel.querySelectorAll('[data-bjaf-dm-view]'));
    const show = (row) => {
      const id = row.dataset.bjafDmCat;
      rows.forEach((r) => r.classList.toggle('is-active', r === row));
      views.forEach((view) => {
        if (view.dataset.bjafDmView === id) view.removeAttribute('hidden');
        else view.setAttribute('hidden', '');
      });
    };
    rows.forEach((row) => {
      row.addEventListener('mouseenter', () => show(row));
      row.addEventListener('focus', () => show(row));
    });
  });
})();
